
import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { UploadCloud } from 'lucide-react';

interface FileUploadDropzoneProps {
  onFilesSelected: (files: File[]) => void;
  maxFiles?: number;
  maxSize?: number;
  accept?: Record<string, string[]>;
  className?: string;
  disabled?: boolean;
}

const FileUploadDropzone: React.FC<FileUploadDropzoneProps> = ({
  onFilesSelected,
  maxFiles = 1,
  maxSize = 10 * 1024 * 1024, // 10MB default
  accept = {
    'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
    'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'text/plain': ['.txt']
  },
  className = '',
  disabled = false
}) => {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    onFilesSelected(acceptedFiles);
  }, [onFilesSelected]);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    maxFiles,
    maxSize,
    accept,
    disabled
  });

  // Get input props without capture attribute to allow user choice on mobile
  // Removing capture attribute gives iPhone users choice between camera and photo library
  const inputProps = getInputProps();

  return (
    <div
      {...getRootProps()}
      className={`border-2 border-dashed rounded-md p-8 text-center cursor-pointer transition-colors ${
        isDragActive ? 'border-primary bg-primary/5' : 
        isDragReject ? 'border-destructive bg-destructive/5' : 
        disabled ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-70' : 
        'border-gray-300 hover:border-primary/50'
      } ${className}`}
    >
      <input {...inputProps} />
      <UploadCloud className={`mx-auto h-12 w-12 ${disabled ? 'text-gray-300' : 'text-gray-400'}`} />
      {isDragActive ? (
        <p className="mt-2 text-sm text-gray-600">Drop the file here...</p>
      ) : isDragReject ? (
        <p className="mt-2 text-sm text-destructive">File type not accepted</p>
      ) : (
        <div>
          <p className={`mt-2 text-sm ${disabled ? 'text-gray-400' : 'text-gray-600'}`}>
            {disabled ? 'File uploads temporarily disabled' : 'Drag and drop a file here, or click to select a file'}
          </p>
          <p className={`mt-1 text-xs ${disabled ? 'text-gray-300' : 'text-gray-500'}`}>
            Maximum file size: {Math.round(maxSize / (1024 * 1024))}MB
          </p>
        </div>
      )}
    </div>
  );
};

export default FileUploadDropzone;
